import { Bell } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Skeleton } from './ui/skeleton';

import {
  useMarkAllRead,
  useMarkRead,
  useMyNotifications,
  useUnreadCount,
} from '@/features/notifications/notifications.api';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${String(mins)}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${String(hrs)}h ago`;
  return `${String(Math.floor(hrs / 24))}d ago`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function NotificationBell(): ReactNode {
  const [open, setOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: unreadData } = useUnreadCount();
  const { data: notifData, isLoading } = useMyNotifications();
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();

  const count = unreadData?.count ?? 0;
  const notifications = notifData?.items ?? [];

  // Close panel on click outside
  useEffect(() => {
    function handler(e: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
    };
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      {/* Bell trigger */}
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="relative rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
        aria-label="Notifications"
      >
        <Bell size={20} />
        {count > 0 && (
          <span className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-0.5 text-[10px] font-semibold leading-none text-white">
            {count > 99 ? '99+' : String(count)}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute right-0 top-10 z-50 w-80 rounded-lg border bg-white shadow-lg">
          <div className="flex items-center justify-between border-b px-4 py-2">
            <span className="text-sm font-semibold">Notifications</span>
            <button
              onClick={() => markAllRead.mutate()}
              className="text-xs text-blue-600 hover:underline"
            >
              Mark all read
            </button>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {isLoading ? (
              <>
                <div className="flex gap-3 px-4 py-3">
                  <Skeleton className="mt-1.5 h-2 w-2 shrink-0 rounded-full" />
                  <div className="flex-1 space-y-1">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
                <div className="flex gap-3 px-4 py-3">
                  <Skeleton className="mt-1.5 h-2 w-2 shrink-0 rounded-full" />
                  <div className="flex-1 space-y-1">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                </div>
                <div className="flex gap-3 px-4 py-3">
                  <Skeleton className="mt-1.5 h-2 w-2 shrink-0 rounded-full" />
                  <div className="flex-1 space-y-1">
                    <Skeleton className="h-4 w-5/6" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
              </>
            ) : notifications.length === 0 ? (
              <div className="flex items-center justify-center py-10 text-sm text-slate-400">
                No notifications yet
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  onClick={() => {
                    if (!n.isRead) markRead.mutate([n.id]);
                  }}
                  className={`flex cursor-pointer gap-3 border-b px-4 py-3 last:border-0 hover:bg-slate-50 ${n.isRead ? 'opacity-60' : ''}`}
                >
                  <div
                    className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${n.isRead ? 'bg-slate-300' : 'bg-blue-500'}`}
                  />
                  <div className="min-w-0">
                    <p className="line-clamp-2 text-sm text-slate-800">{n.body}</p>
                    <p className="mt-0.5 text-xs text-slate-400">
                      {formatRelativeTime(n.createdAt)}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="border-t px-4 py-2 text-center">
            <Link
              to="/notifications"
              className="text-xs text-blue-600 hover:underline"
              onClick={() => {
                setOpen(false);
              }}
            >
              View all notifications
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
